/**
 * 검사 성적서의 상태 — 무엇을 적었고 무엇을 판정했는가.
 *
 * **정본은 서버에 있다**(`GET`·`PUT /api/v1/inspection`, 서버의 `db/models/inspection.ts`).
 * 한때 이 상태를 브라우저(localStorage)에만 두었는데, 그러자 검사한 사람의 브라우저 밖에서는
 * 아무도 결과를 볼 수 없었다(2026-09-18). 지금 브라우저 저장소는 **그 시절에 적어 둔 초안을
 * 서버로 한 번 옮기는 데만** 읽는다 — 서버가 아직 빈 표일 때 한 번 올리고 지운다.
 *
 * 그래서 읽기·지우기를 감싼다. 사생활 보호 창이나 저장이 막힌 환경에서도 검사표는 그대로
 * 떠야 한다.
 */
import type { Results, Verdict } from './checklist.js';

/*
 * v2 — 검사 항목을 새로 짰다(2026-09-17). 항목 이름이 거의 다 바뀌어 v1 의 판정은 새 표에
 * 붙을 자리가 없고, 같은 이름이 남은 몇 줄에 옛 판정이 붙으면 검사자가 찍지 않은 판정이
 * 인쇄된다. 새 열쇠로 빈 표에서 시작한다.
 */
const STORAGE_KEY = 'signageone.inspection.v2';

/** 성적서 머리말 — 이 종이가 어느 납품 건의 것인지. */
export interface ReportHeader {
  /** 문서번호. 비워 두면 인쇄물에 빈칸으로 나간다(손으로 채운다). */
  docNo: string;
  customer: string;
  site: string;
  /** 납품 시스템 표기 — 예: "SignageOne 0.1.0 / 화면 12대". */
  system: string;
  inspectedOn: string;
  inspector: string;
  /** 고객 측 입회자. */
  witness: string;
}

export interface ReportState {
  header: ReportHeader;
  results: Results;
  /** 항목별 비고 — 측정값·예외 사유를 적는다. 불합격 항목은 여기에 근거가 남아야 한다. */
  notes: Readonly<Record<string, string>>;
  /** 종합 소견. */
  conclusion: string;
}

export function emptyState(today: string): ReportState {
  return {
    header: {
      docNo: '',
      customer: '',
      site: '',
      system: '',
      inspectedOn: today,
      inspector: '',
      witness: '',
    },
    results: {},
    notes: {},
    conclusion: '',
  };
}

/** 오늘 날짜(YYYY-MM-DD) — 로컬 기준. 검사일은 검사자가 서 있는 곳의 날짜다. */
export function todayISO(now: Date = new Date()): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const VERDICTS: readonly string[] = ['pass', 'fail', 'na'];

/**
 * 저장된 값을 읽어 온다.
 *
 * **모르는 모양이면 통째로 버린다.** 반쯤 살려 내면 검사자는 자기가 찍었다고 기억하는
 * 판정이 남아 있는지 아닌지 알 수 없게 된다 — 빈 표에서 다시 시작하는 편이 정직하다.
 */
export function parseState(raw: string | null, today: string): ReportState {
  if (!raw) return emptyState(today);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyState(today);
    const source = parsed as Partial<ReportState>;
    const base = emptyState(today);

    const header: ReportHeader = { ...base.header };
    for (const key of Object.keys(base.header) as Array<keyof ReportHeader>) {
      const value = (source.header as Record<string, unknown> | undefined)?.[key];
      if (typeof value === 'string') header[key] = value;
    }

    const results: Record<string, Verdict> = {};
    for (const [id, value] of Object.entries(source.results ?? {})) {
      if (typeof value === 'string' && VERDICTS.includes(value)) results[id] = value as Verdict;
    }

    const notes: Record<string, string> = {};
    for (const [id, value] of Object.entries(source.notes ?? {})) {
      if (typeof value === 'string' && value !== '') notes[id] = value;
    }

    return {
      header,
      results,
      notes,
      conclusion: typeof source.conclusion === 'string' ? source.conclusion : '',
    };
  } catch {
    return emptyState(today);
  }
}

/** 브라우저에 남은 옛 초안. 없거나 모르는 모양이면 빈 표다. */
export function readLegacyDraft(today: string): ReportState {
  try {
    return parseState(window.localStorage.getItem(STORAGE_KEY), today);
  } catch {
    return emptyState(today);
  }
}

/** 옛 초안을 지운다 — 서버로 옮긴 뒤에, 또는 서버에 이미 표가 있어 쓸 일이 없을 때. */
export function clearLegacyDraft(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 지우지 못해도 다음에 서버에 표가 있으면 다시 읽지 않는다.
  }
}
